import { useState } from 'react';
import * as THREE from 'three';
import { type ThreeEvent } from '@react-three/fiber';
import { useSketchStore } from '../../state/useSketchStore';
import { looseEdgeSelectionId } from '../../core/sketch/types';
import { SKETCH_COLORS } from '../../themes/colors';
import { emitSketchDown, emitSketchMove, type SketchHit } from '../../tools/sketch/sketchToolBus';

/**
 * Sketch loose vertex pick layer — the pickable endpoints of drawn segments.
 *
 * SketchUp makes line endpoints first-class picks (hover a start/end point,
 * click to grab it, drag to stretch the line and every welded neighbor).
 * This layer mirrors the solid-vertex pick approach: each loose edge
 * endpoint carries an invisible pick sphere whose radius sits above the edge
 * pick radius, so at a corner the ENDPOINT wins the pointer over the
 * incident edge cylinders (SketchUp endpoint precedence). Hover and
 * selection render as overlay dots on top.
 */

const PICK_RADIUS = 0.07;
const DOT_RADIUS = 0.06;

function LooseVertexPick({
    edgeId,
    end,
    position,
    onHover,
}: {
    edgeId: string;
    end: 'a' | 'b';
    position: THREE.Vector3;
    onHover: (ref: { edgeId: string; end: 'a' | 'b' } | null) => void;
}) {
    const hitOf = (e: ThreeEvent<PointerEvent>): SketchHit => ({
        kind: 'looseVertex',
        edgeId,
        end,
        // The sphere surface can sit up to the pick radius off the true
        // endpoint; tools expect the hit ON the vertex.
        point: position.clone(),
        shiftKey: e.nativeEvent.shiftKey,
        ctrlKey: e.nativeEvent.ctrlKey,
        altKey: e.nativeEvent.altKey,
        button: e.button,
        pointerPx: { x: e.nativeEvent.clientX, y: e.nativeEvent.clientY },
    });

    return (
        <mesh
            position={position}
            visible={false}
            onPointerDown={(e: ThreeEvent<PointerEvent>) => {
                if (e.button !== 0) return;
                e.stopPropagation();
                emitSketchDown(hitOf(e));
            }}
            onPointerMove={(e: ThreeEvent<PointerEvent>) => {
                e.stopPropagation();
                emitSketchMove(hitOf(e));
            }}
            onPointerOver={(e: ThreeEvent<PointerEvent>) => {
                e.stopPropagation();
                onHover({ edgeId, end });
            }}
            onPointerOut={() => onHover(null)}
        >
            <sphereGeometry args={[PICK_RADIUS, 8, 8]} />
        </mesh>
    );
}

function LooseEdgeVertexPicks({ edgeId }: { edgeId: string }) {
    const edge = useSketchStore((s) => s.edges[edgeId]);
    const selected = useSketchStore((s) => s.selectedIds.includes(looseEdgeSelectionId(edgeId)));
    const setHovered = useSketchStore((s) => s.setHovered);
    const [hoveredEnd, setHoveredEnd] = useState<'a' | 'b' | null>(null);

    if (!edge) return null;

    const endpoints: Array<{ end: 'a' | 'b'; position: THREE.Vector3 }> = [
        { end: 'a', position: new THREE.Vector3(...edge.a) },
        { end: 'b', position: new THREE.Vector3(...edge.b) },
    ];

    const handleHover = (ref: { edgeId: string; end: 'a' | 'b' } | null): void => {
        setHoveredEnd(ref?.end ?? null);
        setHovered(ref ? looseEdgeSelectionId(ref.edgeId) : null);
    };

    return (
        <group>
            {endpoints.map((v) => (
                <LooseVertexPick
                    key={v.end}
                    edgeId={edgeId}
                    end={v.end}
                    position={v.position}
                    onHover={handleHover}
                />
            ))}
            {/* Selected segment: its endpoints light up (the SketchUp read
                that a selected line carries its endpoints). */}
            {selected && endpoints.map((v) => (
                <mesh key={`sel-${v.end}`} position={v.position} raycast={() => null} renderOrder={7}>
                    <sphereGeometry args={[DOT_RADIUS, 12, 12]} />
                    <meshBasicMaterial color={SKETCH_COLORS.faceSelected} depthTest={false} transparent opacity={0.95} />
                </mesh>
            ))}
            {/* Hovered endpoint: the SketchUp endpoint read. */}
            {hoveredEnd && (() => {
                const v = endpoints.find((entry) => entry.end === hoveredEnd);
                if (!v) return null;
                return (
                    <mesh position={v.position} raycast={() => null} renderOrder={7}>
                        <sphereGeometry args={[DOT_RADIUS, 12, 12]} />
                        <meshBasicMaterial color={SKETCH_COLORS.primary} depthTest={false} transparent opacity={0.95} />
                    </mesh>
                );
            })()}
        </group>
    );
}

export function SketchLooseVertexPickLayer() {
    const edges = useSketchStore((s) => s.edges);
    return (
        <group>
            {Object.keys(edges).map((id) => (
                <LooseEdgeVertexPicks key={id} edgeId={id} />
            ))}
        </group>
    );
}
